import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { bindActionCreators } from 'redux';
import { List, fromJS } from 'immutable';
import { debounce, isEmpty } from 'underscore';
import { push, replace } from 'connected-react18-router';

import { CHANNEL_SCREEN_POP } from 'src/hooks/useSharedPusher';
import connectOptions, { mergeProps } from '../utils/connectOptions';
import config from '../config';
import Company from '../components/Company';
import stopProp from '../decorators/stopPropagation';
import { getIn, getOriginalIn, isDeepChanged, getChanged, unwrap } from '../utils/ChangeSpy';
import slugify from '../utils/slugify';
import { detectDirty } from '../components/Company/CompanyBuyer/AddOnProject/detectDirty';
import { publish } from '../decorators/tabChange';
import * as companyDetailsActions from '../actions/companyDetail';
import { fetchEventActivities } from '../actions/eventActivities';
import * as companyEventsActions from '../actions/company/companyEvents';
import { getBuyers } from '../actions/company/companyTarget';
import { handleTargetDeleteConfirmation } from '../actions/browse';
import {
  checkAccess,
  canApprove,
  isResearcher,
  isJuniorAnalyst,
  isResearcherIntern,
  isProcessManager,
} from '../utils/checkPermissions';
import checkZip from './services/zipRules';
import naDeletionCheck from './services/deletionCheck';
import CountryDropDown from './CountryDropDown';
import confirm from './decorators/confirm';
import { checkPhone, formatPhone } from './services/phoneRules';
import { showError, showInformation } from '../utils/MessagePopup';
import { DELETE_FAIL_TEXT, DELETION_REQUEST_TEXT } from '../config/constants/common';
import {
  BIC,
  BUSINESS_MODELS,
  BUYER_TAGS_ERROR,
  INDUSTRY_CATEGORIES,
  INDUSTRY_TAGS_ERROR,
} from '../utils/industryTagsHelper';
import { PrettyErrorProvider } from '../contexts/PrettyErrorContext';
import { usePopupContext } from '../contexts/PopupContext';
import { EntityEventContext } from '../contexts/EntityEventContext';

const tabs = config.tabs.get('company');
const values = config.values.get('company');

// First path segment after `/company/:companyId/` for each real tab route (see CompanyRouting.js /
// CompanyBuyerRouting.js). Used to tell an optional `company_slug` segment apart from a real tab
// segment when the URL only has one extra segment (e.g. `/company/123/target` vs `/company/123/acme-corp`).
const COMPANY_TABS = ['target', 'buyer', 'executive'];

const checkLegalName = name => {
  if (!name) return false;

  return /[a-z0-9]/i.test(name);
};

const validate = (
  values,
  { zip: zipBlurred = false, phone: phoneBlurred = false, legalName: legalNameBlurred = false },
) => {
  const { phone, zip, legalName, showCountry } = values;
  const errors = {};

  if (zipBlurred && !showCountry && !checkZip(zip)) {
    errors.zip =
      'Zip code should follow U.S. (XXXXX or XXXXX-XXXX) or Canadian (A1A 1A1) format; otherwise, please set address to International ("...")';
  }

  if (legalNameBlurred && !checkLegalName(legalName)) {
    errors.legalName = 'Company name is not valid.';
  }

  if (phoneBlurred && phone !== '' && !showCountry && !checkPhone(phone)) {
    errors.phone =
      'Phone must be empty or a valid North American area code and format (###-###-####) or +[numbers, spaces and dashes] if international.';
  }

  return errors;
};

/**
 * Split the part of `pathname` after `/company/:companyId` into its pieces, and work out
 * whether the first piece is a `company_slug` (readability only, not a real route segment)
 * or the start of a real tab route (`target`/`buyer`/`executive`).
 *
 * Returns `{ basePrefix, currentSlug, tail, isBare }` where:
 *  - `basePrefix` is `/company/:companyId`
 *  - `currentSlug` is the slug currently in the URL, or `null` if there isn't one
 *  - `tail` is the remaining real route segments (e.g. `['buyer', 'marketing']`)
 *  - `isBare` is true when there's no real tab segment at all, whether or not a slug is
 *    present (e.g. `/company/123` or `/company/123/acme-corp`).
 */
const parseCompanyPath = (companyId, pathname) => {
  const basePrefix = `/company/${companyId}`;
  const segments = pathname
    .slice(basePrefix.length)
    .split('/')
    .filter(Boolean);
  const hasSlug = segments.length > 0 && !COMPANY_TABS.includes(segments[0]);
  const tail = hasSlug ? segments.slice(1) : segments;

  return {
    basePrefix,
    currentSlug: hasSlug ? segments[0] : null,
    tail,
    isBare: tail.length === 0,
  };
};

/**
 * [Private, LBO, Public, VC, ESOP, NFP, Gov, ForPri, ForPub]
 * Those company types don't have parent company
 * Let set parentId = null for them.
 */
const preSaveCheck = data => {
  if ('companyType' in data && config.HAS_PARENT_COMPANY.indexOf(data.companyType) < 0) {
    data.parentId = null;
  }
};

const handleGetSuggestionValue = suggestion => suggestion?.legalName;

const handleRenderSuggestion = suggestion => <div>{suggestion.legalName}</div>;

const handleAddTarget = () => {
  window.open('/company/new/target');
};

const handleAddBuyer = () => {
  window.open('/company/new/buyer');
};

const handleAddExecutive = () => {
  window.open('/contact/new/executive');
};

const handleAddLp = () => {
  window.open('/contact/new/lp');
};

export const CompanyContainer = props => {
  const {
    children,
    match,
    location,
    history,
    user,
    targetCompany,
    project,
    target,
    companyInfo,
    availableTabs,
    buyerType,
    isTarget,
    isDuplicateCompany,
    mergeResultCompanyId,
    isNotApprovedMerge,
    sourceCompanies,
    backloggedActivities,
    inputErrors,
    notifyTab,
    getCompanyInfo,
    getBuyers,
    fetchEventActivities,
    handleTargetDeleteConfirmation,
    handleGetSuggestion,
    handleUpdateCompanyInfo,
    handleUpdateParentCompany,
    handleUpdateCountry,
    revertParentCompany,
    saveCompanyDetail,
    deleteCompany,
    approveTarget,
    confirmMerging,
    confirmMergeRevert,
    enterCompanyEvent,
    leaveCompanyEvent,
    startEditCompanyEvent,
    deleteCompanyEvent,
    reloadEvents,
    push,
    replace,
  } = props;
  const { companyId } = match.params;
  const { openPopup, closePopup, hasPopup } = usePopupContext();

  const [errors, setErrors] = useState({});
  const [hasPermissions] = useState(() => checkAccess(user.getIn(['roles', 0])));
  const [canApproveTarget] = useState(() => canApprove(user.getIn(['roles', 0])));
  const [isOwnerResearcher, setIsOwnerResearcher] = useState(false);
  const [isDisabledOnSave, setIsDisabledOnSave] = useState(false);
  const [canEditData, setCanEditData] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [companyRetainers, setCompanyRetainers] = useState(null);
  const [isCreateNewEventPopupOpened, setIsCreateNewEventPopupOpened] = useState(false);

  const onSaveCallbacksRef = useRef(new Set());
  const validateCallbacksRef = useRef(new Set());

  const isCompanyDeepChanged = useCallback(() => {
    const isChangedCriteriaProjectScope = project.projectData.getIn(['details', 'dirty']);
    const { buyer, info } = targetCompany;
    const retainerCompanyRetainers = buyer.retainer
      .get('companyRetainers')
      .find(companyRetainer => typeof companyRetainer.get('id') === 'string');

    return (
      isDeepChanged(info) ||
      isDeepChanged(target) ||
      isDeepChanged(project) ||
      isDeepChanged(buyer) ||
      Boolean(retainerCompanyRetainers) ||
      isChangedCriteriaProjectScope ||
      detectDirty(targetCompany.buyer.addOnProject)
    );
  }, [project, targetCompany, target]);

  const canDeleteConfirmation = useMemo(() => {
    const userRole = user.getIn(['roles', 0, 'slug']);

    return (
      isResearcher(userRole) || isResearcherIntern(userRole) || isJuniorAnalyst(userRole) || isProcessManager(userRole)
    );
  }, [user]);

  const deleteConfirmText = canDeleteConfirmation
    ? 'Are you sure you want to request your supervisor to delete this company? After the supervisor approval, the company and all associate data will be permanently deleted.'
    : 'Delete this company?';

  const updateCanEditData = () => {
    const roleIsResearcher = isResearcher(user.getIn(['roles', 0, 'slug']));
    const isUnapproved = !companyInfo.get('isApproved');
    const currentStatusValue = Number(unwrap(target.getIn(['buyers', 0, 'currentStatus'])));

    if (isDuplicateCompany) {
      setCanEditData(false);

      return;
    }

    if (!(!roleIsResearcher && isUnapproved)) {
      if (currentStatusValue < 2) {
        setCanEditData(true);
      } else if (isOwnerResearcher) {
        setCanEditData(true);
      } else if (hasPermissions) {
        setCanEditData(true);
      }
    } else if (canEditData) {
      setCanEditData(false);
    }
  };

  const addOnSaveCallback = useCallback(callback => {
    onSaveCallbacksRef.current.add(callback);
  }, []);

  const removeOnSaveCallback = useCallback(callback => {
    onSaveCallbacksRef.current.delete(callback);
  }, []);

  const addValidateCallBacks = useCallback(callback => {
    validateCallbacksRef.current.add(callback);
  }, []);

  const deleteValidateCallbacks = useCallback(callback => {
    validateCallbacksRef.current.delete(callback);
  }, []);

  /**
   * If users change company type from "visible-parent" to "none-parent", revert parent company value to the original value.
   */
  const switchCompanyTypeCheck = useCallback(
    value => {
      const prevCompanytype = targetCompany.info.getIn(['info', 'companyType', 'value']);

      if (config.HAS_PARENT_COMPANY.indexOf(prevCompanytype) > -1 && config.HAS_PARENT_COMPANY.indexOf(value) < 0) {
        revertParentCompany();
      }
    },
    [targetCompany, revertParentCompany],
  );

  const handleCompanyInfoChange = useCallback(
    event => {
      const { name, value } = event.target;

      if (name === 'country') return;

      const updateInfo = {
        [name]: value,
      };

      if (name === 'companyType') {
        switchCompanyTypeCheck(value);
      }

      handleUpdateCompanyInfo(updateInfo);
    },
    [switchCompanyTypeCheck, handleUpdateCompanyInfo],
  );

  const handleToggleDBA = useCallback(() => {
    const currentValue = targetCompany.info.getIn(['info', 'showDBA']);

    handleUpdateCompanyInfo({
      showDBA: !currentValue,
    });
  }, [targetCompany, handleUpdateCompanyInfo]);

  /**
   * If users show off or show on country, clear all address information.
   */
  const handleToggleCountry = useCallback(() => {
    const showCountry = targetCompany.info.getIn(['info', 'showCountry']);

    handleUpdateCompanyInfo({ city: '' });
    handleUpdateCompanyInfo({ zip: '' });
    handleUpdateCompanyInfo({ state: '' });
    handleUpdateCountry({
      text: '',
      selected: {
        id: null,
        text: '',
      },
    });
    handleUpdateCompanyInfo({
      showCountry: !showCountry,
    });
  }, [targetCompany, handleUpdateCompanyInfo, handleUpdateCountry]);

  const handleParentCompanyClick = useCallback(() => {
    const selectedCompany = targetCompany.info.get('parentCompany').get('selected');

    if (selectedCompany) {
      window.open(`/company/${selectedCompany.get('id')}`);
    }
  }, [targetCompany]);

  const handleUpdateSuggestions = useCallback(
    update => {
      handleUpdateParentCompany(update);
    },
    [handleUpdateParentCompany],
  );

  const fetchSuggestions = useCallback(
    ({ value }) => {
      handleGetSuggestion(value);
    },
    [handleGetSuggestion],
  );

  const handleSuggestionFetch = useMemo(() => debounce(fetchSuggestions, 500), [fetchSuggestions]);

  const handleSuggestionSelected = useCallback(
    (event, { suggestion }) => {
      handleUpdateParentCompany({
        text: suggestion.legalName,
        selected: suggestion,
        suggestions: [],
      });
    },
    [handleUpdateParentCompany],
  );

  const handleUpdateCountrySuggestions = useCallback(
    event => {
      handleUpdateCountry({
        text: event.target.value,
        selected: null,
      });
    },
    [handleUpdateCountry],
  );

  const handleCountrySuggestionSelected = useCallback(
    selection => {
      handleUpdateCountry({
        text: selection.text,
        selected: selection,
      });
    },
    [handleUpdateCountry],
  );

  const handleEventMouseEnter = useCallback(
    (event, eventId) => {
      const { target: eventTarget, clientX: left } = event;
      const rect = eventTarget.getBoundingClientRect();
      const { bottom: top } = rect;

      enterCompanyEvent({ eventId, left, top });
    },
    [enterCompanyEvent],
  );

  const handleEventMouseLeave = useCallback(
    (event, eventId) => {
      leaveCompanyEvent({ eventId });
    },
    [leaveCompanyEvent],
  );

  const handleAddEvent = useCallback(() => {
    if (!canEditData) return;

    const { pathname } = location;
    const name = 'EventCompanyPopup';

    const isBuyerRoute = pathname.includes('/buyer');
    const isTargetRoute = pathname.includes('/target');

    if (!isTargetRoute && !isBuyerRoute) return;

    if (isTargetRoute) {
      openPopup(name, {
        event: null,
        approach: 'target',
        companyInfo: targetCompany.info.getIn(['info']),
        companyBuyer: targetCompany.target.getIn(['buyers', 0]),
        companyContacts: targetCompany.target.getIn(['contacts']),
        onReopen: handleAddEvent,
      });
    } else if (isBuyerRoute) {
      openPopup(name, {
        event: null,
        approach: 'buyer',
        onReopen: handleAddEvent,
      });
    }
  }, [canEditData, location, targetCompany, openPopup]);

  const handleEditEvent = useCallback(
    (mouseEvent, event) => {
      if (!canEditData) return;

      const eventId = event.get('id');

      startEditCompanyEvent({
        eventId,
        afterSuccess: () => {
          if (eventId && !event.get('meta')) {
            openPopup('EventCompanyPopup', {
              event,
              onReopen: handleAddEvent,
            });
          }
        },
        afterError: ({ responseBody }) => {
          showError(openPopup, [responseBody?.message || 'An error occurred']);
        },
      });
    },
    [canEditData, startEditCompanyEvent, openPopup, handleAddEvent],
  );

  const deleteEventAfterConfirm = useCallback(
    (mouseEvent, event) => {
      if (!canEditData) return;

      const { id } = event;

      deleteCompanyEvent({
        id,
        entityId: companyId,
        afterSuccess: () => {
          notifyTab({ id: event.id }, 'onEventChange');
          closePopup();
        },
      });
    },
    [canEditData, companyId, deleteCompanyEvent, notifyTab, closePopup],
  );

  const handleDeleteEvent = useMemo(
    () =>
      stopProp(
        naDeletionCheck(user, confirm('Delete this event?', deleteEventAfterConfirm, { openPopup, closePopup }), {
          openPopup,
          closePopup,
        }),
      ),
    [user, deleteEventAfterConfirm, openPopup, closePopup],
  );

  const handleErrorClose = useCallback((event, field) => {
    if (['phone', 'zip', 'legalName'].indexOf(field) > -1) {
      setErrors({ [field]: '' });
    }
  }, []);

  const deleteCompanyAfterConfirm = useCallback(() => {
    if (canDeleteConfirmation) {
      handleTargetDeleteConfirmation({
        body: {
          params: {
            selected_ids: [companyId],
          },
        },
        callback: {
          beforeStart: () => {},
          afterSuccess: () => {
            showInformation(openPopup, DELETION_REQUEST_TEXT);
          },
          afterError: () => {
            showError(openPopup, [DELETE_FAIL_TEXT]);
          },
        },
      });
    } else {
      deleteCompany({ companyId, afterSuccess: closePopup });
    }
  }, [canDeleteConfirmation, companyId, handleTargetDeleteConfirmation, openPopup, deleteCompany, closePopup]);

  const handleCompanyDelete = useMemo(
    () => stopProp(confirm(deleteConfirmText, deleteCompanyAfterConfirm, { openPopup, closePopup })),
    [deleteConfirmText, deleteCompanyAfterConfirm, openPopup, closePopup],
  );

  const validateForm = useCallback(
    (blurred = {}) => {
      const info = targetCompany.info.get('info');
      const legalName = unwrap(info.get('legalName'));
      const phone = unwrap(info.get('phone'));
      const zip = unwrap(info.get('zip'));
      const showCountry = info.getIn(['showCountry']);
      const validationErrors = validate(
        {
          legalName,
          zip,
          phone,
          showCountry,
        },
        blurred,
      );

      setErrors(validationErrors);

      return validationErrors;
    },
    [targetCompany],
  );

  const handleBlurInfo = useCallback(() => {
    const info = getChanged(targetCompany.info.get('info'));
    const criteria = getChanged(targetCompany.buyer.criteria);
    const buyer = getChanged(targetCompany.buyer.info);
    const validationErrors = validateForm({
      zip: true,
      phone: true,
      legalName: true,
    });

    delete criteria.businessModels;
    delete criteria.industryCategories;

    if (isEmpty(info) && isEmpty(criteria) && isEmpty(buyer)) return;

    if (isEmpty(validationErrors)) {
      const body = { ...info, ...criteria, ...buyer };

      if ('parentId' in body) {
        body.parentId = body.parentId || null;
      }

      if ('country' in body) {
        body.countryId = body.country.countryId;
        delete body.country;
      }

      if ('address' in body) {
        body.address = body.address.replace(/[\r\n\t]/g, ' ');
      }

      if (Object.keys(body).length) {
        preSaveCheck(body);
        saveCompanyDetail({ companyId, body });
      }
    }
  }, [targetCompany, validateForm, saveCompanyDetail, companyId]);

  const handleBlur = useCallback(
    ({ target: { name, value = '' } }) => {
      const blurErrors = validateForm({ [name]: true });

      if (name === 'phone') {
        const showCountry = targetCompany.info.getIn(['info', 'showCountry']);

        if (!blurErrors.phone) {
          handleUpdateCompanyInfo({
            phone: showCountry ? value : formatPhone(value),
          });
        }
      }
    },
    [validateForm, targetCompany, handleUpdateCompanyInfo],
  );

  const confirmTags = useCallback(
    event => {
      const { buyer, isValid } = targetCompany;
      let error = '';
      const { pathname } = location;

      if (isValid) {
        setIsDisabledOnSave(false);
      }

      if (pathname.includes('target')) {
        const targetTags = unwrap(target.get(INDUSTRY_CATEGORIES));
        const targetBMs = unwrap(target.get(BUSINESS_MODELS));
        const targetHasTags = !!targetTags.size && !!targetBMs.size;

        if (!targetHasTags) {
          error = INDUSTRY_TAGS_ERROR;
          setIsDisabledOnSave(false);
        }
      }

      if (pathname.includes('buyer')) {
        if (pathname.includes('criteria')) {
          const buyerTags = buyer.criteria.get(BIC);

          if (!buyerTags.size) {
            error = BUYER_TAGS_ERROR;
            setIsDisabledOnSave(false);
          }
        } else if (pathname.includes('add-on-project')) {
          const buyerAddOnProjectTags = unwrap(buyer.addOnProject.tags.get(INDUSTRY_CATEGORIES));
          const buyerAddOnProjectBMs = unwrap(buyer.addOnProject.tags.get(BUSINESS_MODELS));

          const buyerAddOnProjectHasTags = !!buyerAddOnProjectTags.size && !!buyerAddOnProjectBMs.size;

          if (!buyerAddOnProjectHasTags) {
            error = INDUSTRY_TAGS_ERROR;
            setIsDisabledOnSave(false);
          }
        }
        validateCallbacksRef.current.forEach(callback => callback && callback(event));
      }

      return error;
    },
    [targetCompany, location, target],
  );

  const handleSave = useCallback(
    event => {
      setIsDisabledOnSave(true);
      setCompanyRetainers(null);
      if (availableTabs.get('target') || availableTabs.get('buyer')) {
        event.persist();

        const error = confirmTags(event);

        if (error.length === 0) {
          handleBlurInfo();
          onSaveCallbacksRef.current.forEach(callback => callback && callback(event));
        } else {
          openPopup('ErrorPopup', { error });
        }
      } else {
        handleBlurInfo();
        onSaveCallbacksRef.current.forEach(callback => callback && callback(event));
      }
    },
    [availableTabs, confirmTags, handleBlurInfo, openPopup],
  );

  /**
   * If company doesn't have target role, so activating target role popup
   * Financial Buyer can't activate target role.
   */
  const ifTarget = useCallback(() => {
    if (isNaN(companyId)) return push('/');

    if (!isTarget) {
      if (!hasPermissions) {
        openPopup('InformationPopup', {
          message: {
            texts: ["You don't have permission for this action"],
            className: 'text-danger',
          },
          header: (
            <span>
              <i aria-hidden="true" className="fa fa-exclamation-triangle text-danger" />
              Error Alert
            </span>
          ),
        });

        return false;
      }

      if (buyerType === 'financial') {
        openPopup('ActiveTargetPopup', {
          isFinancialBuyer: true,
          companyId,
        });

        return false;
      }

      openPopup('ActiveTargetPopup', {
        isFinancialBuyer: false,
        companyId,
      });

      return false;
    }

    return true;
  }, [companyId, isTarget, hasPermissions, buyerType, push, openPopup]);

  const handleTabClick = useCallback(
    ({ name, to }) => {
      if (targetCompany.info.get('isFetching')) return;

      if (isDuplicateCompany) return;

      if (name === 'TARGET') {
        if (ifTarget()) {
          push(to);
        }
      } else if (name === 'TRADE GROUPS') {
        window.open(to, '_blank');
      } else {
        push(to);
      }
    },
    [targetCompany, isDuplicateCompany, ifTarget, push],
  );

  const handleApproveTarget = useCallback(() => {
    const companyRecordId = companyInfo.get('id');
    const afterSuccess = () => closePopup();
    const afterError = () => closePopup();

    openPopup('ConfirmPopup', {
      message: 'Are you sure to confirm the company?',
      onOk: () => approveTarget(companyRecordId, afterSuccess, afterError),
      onCancel: () => {
        closePopup();
      },
      yes: 'Yes',
      no: 'No',
    });
  }, [companyInfo, closePopup, openPopup, approveTarget]);

  /** Handle confirm companies merging click. */
  const handleConfirmMerging = useCallback(() => {
    const companyRecordId = companyInfo.get('id');
    const afterSuccess = () => closePopup();
    const afterError = () => closePopup();

    openPopup('ConfirmPopup', {
      message: 'Are you sure to confirm the merging?',
      onOk: () => confirmMerging(companyRecordId, afterSuccess, afterError),
      onCancel: () => closePopup(),
      yes: 'Yes',
      no: 'No',
    });
  }, [companyInfo, closePopup, openPopup, confirmMerging]);

  /** Handle confirm companies revert merging click. */
  const handleConfirmMergeRevert = useCallback(() => {
    const companyRecordId = companyInfo.get('id');

    const afterSuccess = () => {
      closePopup();
      history.replace('/');
    };
    const afterError = () => closePopup();

    openPopup('ConfirmPopup', {
      message: 'Are you sure you want to revert merge result for this company?',
      onOk: () => confirmMergeRevert(companyRecordId, afterSuccess, afterError),
      onCancel: () => closePopup(),
      yes: 'Yes',
      no: 'No',
    });
  }, [companyInfo, closePopup, history, openPopup, confirmMergeRevert]);

  /** Handle reload company events. */
  const handleReloadEvents = useCallback(() => {
    if (hasPopup()) {
      return;
    }

    reloadEvents({ entityId: companyId });
  }, [hasPopup, reloadEvents, companyId]);

  const tryRedirect = () => {
    if (availableTabs.get('target')) return setTimeout(() => replace(`/company/${companyId}/target`), 0);
    if (availableTabs.get('buyer')) return setTimeout(() => replace(`/company/${companyId}/buyer/marketing`), 0);
    if (availableTabs.get('exec')) return setTimeout(() => replace(`/company/${companyId}/executive`), 0);
    if (availableTabs.get('tradeGroups')) return setTimeout(() => replace(`/tradeGroups`), 0);
  };

  /**
   * Keep the `company_slug` segment of the URL in sync with the company's legal name.
   * This is for readability only (sharing/revisiting links) - it never affects data
   * loading or route matching, since `companyId` is always parsed separately.
   */
  const trySlugRedirect = () => {
    if (isNaN(Number(companyId))) return;
    if (targetCompany.info.get('isFetching')) return;

    const desiredSlug = slugify(getOriginalIn(targetCompany.info, 'info.legalName', ''));

    if (!desiredSlug) return;

    const { basePrefix, currentSlug, tail } = parseCompanyPath(companyId, location.pathname);

    if (currentSlug === desiredSlug) return;

    const newPathname = `${basePrefix}/${desiredSlug}${tail.length ? `/${tail.join('/')}` : ''}`;

    if (newPathname !== location.pathname) replace(`${newPathname}${location.search}`);
  };

  useEffect(() => {
    fetchEventActivities();
    getCompanyInfo({ companyId });
    getBuyers({ companyId });
    if (companyInfo.get('buyers')) updateCanEditData();
    setIsDisabledOnSave(false);
  }, []);

  const hasMountedRef = useRef(false);
  const previousUpdateRef = useRef({
    companyInfo,
    companyRetainers: targetCompany.buyer.retainer.get('companyRetainers'),
  });

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      previousUpdateRef.current = {
        companyInfo,
        companyRetainers: targetCompany.buyer.retainer.get('companyRetainers'),
      };

      return;
    }

    const { companyInfo: previousCompanyInfo, companyRetainers: previousCompanyRetainers } = previousUpdateRef.current;
    const currentCompanyRetainers = targetCompany.buyer.retainer.get('companyRetainers');

    previousUpdateRef.current = { companyInfo, companyRetainers: currentCompanyRetainers };

    const isValid = targetCompany.target.get('isValid');

    if (isValid) {
      setIsDisabledOnSave(false);
    }

    if (!isCompanyDeepChanged() && isDisabledOnSave) {
      setIsDisabledOnSave(false);
    }

    const researcherId = companyInfo.get('createdResearcherId');
    const isOwnerResearcherValue = researcherId === user.get('id');
    const currentStatusValue = Number(unwrap(target.getIn(['buyers', 0, 'currentStatus'])));

    trySlugRedirect();

    if (targetCompany.info.get('isFetching') === true) return;

    // The original class also checked `oldProps.info && oldProps.info.get('isFetching') === false`
    // here, but `info` was never an actual prop on this container (only `companyInfo`/`targetCompany.info`
    // are) - that condition was always `undefined && ...`, i.e. permanently dead. Omitted rather than
    // faked with a ref that would also always be undefined.

    if (parseCompanyPath(companyId, location.pathname).isBare) tryRedirect();
    if (isOwnerResearcher !== isOwnerResearcherValue) setIsOwnerResearcher(isOwnerResearcherValue);
    if (!isNaN(currentStatusValue) && currentStatus !== currentStatusValue) setCurrentStatus(currentStatusValue);
    if (!previousCompanyInfo.get('buyers') && companyInfo.get('buyers')) updateCanEditData();

    const isChanged = previousCompanyRetainers !== currentCompanyRetainers;

    if (companyRetainers === null && isChanged) {
      setCompanyRetainers(currentCompanyRetainers.toJS());
    }

    if (canEditData) {
      const searchParams = new URLSearchParams(location.search);
      const isCreateNewEvent = searchParams.has('createNewEvent');

      if (isCreateNewEvent && !isCreateNewEventPopupOpened) {
        setIsCreateNewEventPopupOpened(true);
        handleAddEvent();
      }
    }
  });

  const entityEventContextValue = useMemo(
    () => ({
      addOnSaveCallback,
      removeOnSaveCallback,
      addValidateCallBacks,
      deleteValidateCallbacks,
      onAddEvent: handleAddEvent,
      onEditEvent: handleEditEvent,
      onDeleteEvent: handleDeleteEvent,
      entityId: Number(companyId),
    }),
    [
      addOnSaveCallback,
      removeOnSaveCallback,
      addValidateCallBacks,
      deleteValidateCallbacks,
      handleAddEvent,
      handleEditEvent,
      handleDeleteEvent,
      companyId,
    ],
  );

  const { info, events } = targetCompany;
  const companyInfoProps = {
    companyInfoProps: {
      onSubmit: handleBlurInfo,
      onCompanyInfoChange: handleCompanyInfoChange,
      onToggleDBA: handleToggleDBA,
      onToggleCountry: handleToggleCountry,
      onParentCompanyClick: handleParentCompanyClick,
    },
    companyEventProps: {
      events,
      onMouseEnter: handleEventMouseEnter,
      onMouseLeave: handleEventMouseLeave,
    },
    companyMergeInfoProps: {
      isDuplicateCompany,
      mergeResultCompanyId,
      isNotApprovedMerge,
      sourceCompanies,
      onConfirmMerging: handleConfirmMerging,
      onConfirmMergeRevert: handleConfirmMergeRevert,
    },
  };
  const suggestions = info.getIn(['parentCompany', 'suggestions'], fromJS([]));
  const text = info.getIn(['parentCompany', 'text']) || '';
  const suggestionProps = {
    suggestionProps: {
      name: 'parentCompany',
      inputProps: {
        className: 'parentCompany form-control',
        id: 'parentCompany',
        name: 'parentCompany',
        placeholder: 'Parent Company Name',
        disabled: !canEditData,
      },
      suggestions,
      onUpdateSuggestions: handleUpdateSuggestions,
      getSuggestion: handleSuggestionFetch,
      getSuggestionValue: handleGetSuggestionValue,
      renderSuggestion: handleRenderSuggestion,
      onSuggestionSelected: handleSuggestionSelected,
      checkOn: 'text',
      text,
      loading: info.getIn(['parentCompany', 'loading'], false),
    },
  };

  const countrySuggestion = (
    <CountryDropDown
      disabled={!canEditData}
      name="country"
      onChange={handleUpdateCountrySuggestions}
      onSelect={handleCountrySuggestionSelected}
      value={info.getIn(['info', 'country', 'countryName'], '')}
    />
  );

  const footerProps = {
    footer: {
      isDisabledOnSave,
      onNewTarget: handleAddTarget,
      onNewBuyer: handleAddBuyer,
      onNewExecutive: handleAddExecutive,
      onNewLp: handleAddLp,
      onDelete: handleCompanyDelete,
      hideSave: !isCompanyDeepChanged(),
      onSave: handleSave,
      onApproveTarget: handleApproveTarget,
    },
  };

  return (
    <div className="full-height">
      <Helmet title={`${getIn(info, 'info.legalName', '')} [Company Page]`.trim()} />
      <EntityEventContext.Provider value={entityEventContextValue}>
        <PrettyErrorProvider inputErrors={inputErrors} onErrorClose={handleErrorClose}>
          <Company
            {...props}
            {...suggestionProps}
            {...footerProps}
            {...companyInfoProps}
            backloggedActivities={backloggedActivities}
            canApproveTarget={canApproveTarget}
            canEditData={canEditData}
            countrySuggestion={countrySuggestion}
            error={{ ...errors }}
            hasPermissions={hasPermissions}
            onBlur={handleBlur}
            onClick={handleTabClick}
            onErrorClose={handleErrorClose}
            onReloadEvents={handleReloadEvents}
            params={match.params}
          >
            {children}
          </Company>
        </PrettyErrorProvider>
      </EntityEventContext.Provider>
    </div>
  );
};

CompanyContainer.propTypes = {
  confirmMergeRevert: PropTypes.func.isRequired,
  confirmMerging: PropTypes.func.isRequired,
  getCompanyInfo: PropTypes.func.isRequired,
  handleGetSuggestion: PropTypes.func.isRequired,
  handleUpdateCompanyInfo: PropTypes.func.isRequired,
  handleUpdateParentCompany: PropTypes.func.isRequired,
  isDuplicateCompany: PropTypes.bool.isRequired,
  isNotApprovedMerge: PropTypes.bool.isRequired,
  mergeResultCompanyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  sourceCompanies: PropTypes.instanceOf(List).isRequired,
  targetCompany: PropTypes.object.isRequired,
};

const getAvailableTabs = (function() {
  let availableTabs = fromJS({
    target: false,
    buyer: false,
    exec: false,
    tradeGroups: false,
  });

  return function(state) {
    availableTabs = availableTabs.set('target', state.targetCompany.target.get('isTarget'));
    availableTabs = availableTabs.set('buyer', !!state.targetCompany.buyer.info.get('buyerType'));
    availableTabs = availableTabs.set('exec', state.targetCompany.executive.get('queryResults').size > 0);

    return availableTabs;
  };
})();

const mapStateToProps = state => ({
  availableTabs: getAvailableTabs(state),
  targetCompany: state.targetCompany,
  project: state.project,
  target: state.targetCompany.target,
  companyInfo: state.targetCompany.info.get('info'),
  tabs,
  values,
  buyerType: state.targetCompany.buyer.info.get('buyerType', '').toLowerCase(),
  isTarget: state.targetCompany.target.get('isTarget', false),
  isDuplicateCompany: state.targetCompany.mergeInfo.get('isDuplicateCompany'),
  mergeResultCompanyId: state.targetCompany.mergeInfo.get('mergeResultCompanyId'),
  isNotApprovedMerge: state.targetCompany.mergeInfo.get('isNotApprovedMerge'),
  sourceCompanies: state.targetCompany.mergeInfo.get('sourceCompanies'),
  user: state.auth.get('user', null),
  screenPopChannel: state.dialPadPusher.get('data', null)?.find(channel => channel.get('type') === CHANNEL_SCREEN_POP),
  backloggedActivities: state.eventActivities.get('backloggedActivityValues', List()),
});

const mapDispatchToProps = dispatch => ({
  ...bindActionCreators(companyDetailsActions, dispatch),
  ...bindActionCreators(companyEventsActions, dispatch),
  ...bindActionCreators({ getBuyers }, dispatch),
  ...bindActionCreators({ handleTargetDeleteConfirmation }, dispatch),
  ...bindActionCreators({ fetchEventActivities }, dispatch),
  ...bindActionCreators({ replace, push }, dispatch),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
  connectOptions,
)(publish(memo(CompanyContainer)));
